import { styled } from "../../../styles";

/**
 * Styles for the /admin Settings group — AI Prompt Templates (EN-5161) and
 * Custom Email Templates (EN-5162). Generic admin chrome lives in
 * `shared/AdminPage.styles.ts`.
 */

/** Prose inside a confirmation modal. */
export const ModalBodyText = styled("p", {
  margin: 0,
  fontSize: "14px",
  lineHeight: 1.5,
  color: "$charcoal",
  fontFamily: "$openSans",
});

/** Card holding the editor + preview once a template is selected. */
export const TemplateCard = styled("section", {
  display: "flex",
  flexDirection: "column",
  gap: "16px",
  padding: "16px",
  borderRadius: "8px",
  border: "1px solid $gray-30",
  backgroundColor: "$white",
});

/**
 * Footer of the template card: the shared-default warning, then the origin
 * caption and the actions. All three describe what Save does, so they sit
 * with the button rather than above the editor (EN-5146 UI feedback).
 */
export const TemplateCardFooter = styled("div", {
  display: "flex",
  flexDirection: "column",
  gap: "12px",
  paddingTop: "16px",
  borderTop: "1px solid $gray-30",

  /**
   * Sticks to the bottom of the scrolling content area so Save is reachable
   * whatever the editor's height (EN-5146 UI feedback). The pane below is
   * sized against the viewport, but the textarea is also user-resizable —
   * any height that merely "usually fits" can be dragged past, and then the
   * only button on the page is off screen.
   *
   * Negative side margins + matching padding let the opaque background reach
   * the card's edges, so content scrolls under the bar rather than beside it.
   */
  position: "sticky",
  bottom: 0,
  zIndex: 1,
  backgroundColor: "$white",
  margin: "0 -16px -16px",
  padding: "16px",
  // Reaching the card's edges means matching its corners.
  borderRadius: "0 0 8px 8px",
});

export const TemplateCardActionRow = styled("div", {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "16px",
  flexWrap: "wrap",
});

/**
 * Subject and its Published flag on one line. The subject input ran the full
 * width of the card with a checkbox stranded on its own row underneath, and
 * the vertical space that cost came straight off the editor below (EN-5146
 * UI feedback).
 */
export const SubjectRow = styled("div", {
  display: "flex",
  // Both columns are label-over-control, so their controls line up.
  alignItems: "flex-end",
  gap: "24px",
  flexWrap: "wrap",
});

/** The subject half: takes whatever the flag doesn't. */
export const SubjectField = styled("div", {
  display: "flex",
  flexDirection: "column",
  gap: "6px",
  flex: 1,
  // Without this a long subject pushes the flag off the row.
  minWidth: "280px",
});

export const VisibilityField = styled("div", {
  display: "flex",
  flexDirection: "column",
  gap: "6px",
  flexShrink: 0,
});

/**
 * Checkbox and its explanation side by side, so the block is exactly as tall
 * as the subject field beside it and the two controls sit on one line.
 */
export const VisibilityControl = styled("div", {
  display: "flex",
  alignItems: "center",
  gap: "10px",
});

export const TemplateCardActions = styled("div", {
  display: "flex",
  gap: "8px",
  alignItems: "center",
});

/**
 * Says whether the loaded template is the ward's own or the shared default —
 * saving a default creates the ward's copy, which Retool never made clear.
 */
export const TemplateOrigin = styled("span", {
  fontFamily: "$openSans",
  fontSize: "12px",
  color: "$gray-80",
});

/** Empty state before both selects are set. */
export const SelectionPrompt = styled("p", {
  margin: 0,
  padding: "48px 16px",
  textAlign: "center",
  fontFamily: "$openSans",
  fontSize: "14px",
  color: "$gray-80",
  borderRadius: "8px",
  border: "1px dashed $gray-40",
  backgroundColor: "$white",
});
